import { auth } from '../../utils/firebase';
import RegisterForm from './RegisterForm';
import React from 'react';
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    updateProfile, 
    setPersistence, 
    browserLocalPersistence, 
    signOut,
    sendPasswordResetEmail
} from 'firebase/auth';
import logo from '../../img/logo.png';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';

const LoginView = ({ onLoginSuccess, onClose, onRegistrationCompleteRedirect, onProfileUpdate }) => {
    const [isRegistering, setIsRegistering] = React.useState(false);
    const [tempUser, setTempUser] = React.useState(null);
    const [mode, setMode] = React.useState('signin');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [name, setName] = React.useState('');
    const [error, setError] = React.useState('');
    const [resetEmailSent, setResetEmailSent] = React.useState(false);
    const [isResettingPassword, setIsResettingPassword] = React.useState(false);

    const handleSignIn = async (e) => {
        e.preventDefault();
        try {
            await signInWithEmailAndPassword(auth, email, password);
            onLoginSuccess?.();
        } catch (error) {
            setError('Invalid email or password. Please try again.');
            console.error("Error signing in:", error);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        try {
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            // Update user profile
            await updateProfile(userCredential.user, {
                displayName: name
            });
            
            // Create user document with onboarding state
            const userDocRef = doc(db, 'users', userCredential.user.uid);
            await setDoc(userDocRef, {
                email: userCredential.user.email,
                name: name,
                hasSeenOnboarding: false, // Initialize onboarding state
                // ... other user data ...
            });
            
            setTempUser(userCredential.user);
            setIsRegistering(true);
            

        } catch (error) {
            setError(error.message);
            console.error("Error creating account:", error);
        }
    };

    const handleRegistrationComplete = async () => {
        try {
            setIsRegistering(false);
            onRegistrationCompleteRedirect();
        } catch (error) {
            console.error("Error signing in after registration:", error);
            setError('Error completing registration. Please try signing in.');
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        try {
            await sendPasswordResetEmail(auth, email);
            setResetEmailSent(true);
            setError('');
        } catch (error) {
            setError('Error sending password reset email. Please try again.');
            console.error("Error sending reset email:", error);
        }
    };

    if (isRegistering && tempUser) {
        return <RegisterForm 
            user={tempUser} 
            onComplete={handleRegistrationComplete} 
            onRegistrationCompleteRedirect={onRegistrationCompleteRedirect}
            onProfileUpdate={onProfileUpdate} 
        />;
    }

    if (isResettingPassword) {
        return (
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-8 relative mx-4">
                    <button 
                        onClick={() => {
                            setIsResettingPassword(false);
                            setResetEmailSent(false);
                            setError('');
                        }}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="text-center">
                        <img 
                            src={logo} 
                            alt="Logo" 
                            className="mx-auto h-12 w-auto"
                        />
                        <h2 className="mt-6 text-2xl font-bold text-gray-900">
                            Reset Password
                        </h2>
                    </div>

                    {resetEmailSent ? (
                        <div className="mt-8 text-center">
                            <div className="text-green-600 mb-4">
                                Password reset email sent! Please check your inbox.
                            </div>
                            <button
                                onClick={() => {
                                    setIsResettingPassword(false);
                                    setResetEmailSent(false);
                                }}
                                className="text-purple-600 hover:text-purple-700"
                            >
                                Return to login
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleForgotPassword} className="mt-8 space-y-6">
                            {error && (
                                <div className="text-red-500 text-sm text-center">
                                    {error}
                                </div>
                            )}
                            <div>
                                <label htmlFor="reset-email" className="sr-only">
                                    Email address
                                </label>
                                <input
                                    id="reset-email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                    placeholder="Email address"
                                />
                            </div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                Send Reset Link
                            </button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-8 relative mx-4">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                
                <div className="text-center">
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className="mx-auto h-12 w-auto"
                    />
                    <p className="mt-2 text-sm text-gray-600">
                        {mode === 'signin' ? 'Log in to your account' : 'Create a new account'}
                    </p>
                </div>

                {/* Toggle buttons */}
                <div className="flex justify-center space-x-4 mb-8">
                    <button
                        onClick={() => {
                            setMode('signin');
                            setError('');
                        }}
                        className={`px-4 py-2 rounded-full ${
                            mode === 'signin' 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-gray-200 text-gray-700'
                        }`}
                    >
                        Log In
                    </button>
                    <button
                        onClick={() => {
                            setMode('signup');
                            setError('');
                        }}
                        className={`px-4 py-2 rounded-full ${
                            mode === 'signup' 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-gray-200 text-gray-700'
                        }`}
                    >
                        Create Account
                    </button>
                </div>

                {error && (
                    <div className="text-red-500 text-sm text-center">
                        {error}
                    </div>
                )}

                <form onSubmit={mode === 'signin' ? handleSignIn : handleSignUp} className="mt-8 space-y-6">
                    <div className="rounded-md shadow-sm space-y-4">
                        {mode === 'signup' && (
                            <div>
                                <label htmlFor="name" className="sr-only">
                                    Full Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                    placeholder="Full Name"
                                />
                            </div>
                        )}
                        <div>
                            <label htmlFor="email" className="sr-only">
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                placeholder="Email address"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                placeholder="Password"
                            />
                        </div>
                        {mode === 'signup' && (
                            <div>
                                <label htmlFor="confirmPassword" className="sr-only">
                                    Confirm Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                                    placeholder="Confirm Password"
                                />
                            </div>
                        )}
                    </div>

                    <div className="text-sm text-right mb-4">
                        <button
                            type="button"
                            onClick={() => setIsResettingPassword(true)}
                            className="text-purple-600 hover:text-purple-700"
                        >
                            Forgot password?
                        </button>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                            {mode === 'signin' ? 'Log in' : 'Create Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginView; 